/**
 * Rail shapes (vanilla RailShape / RailState). Every shape is its own block id (blocks.ts RAIL_FAMILIES, like
 * stairs and fences), so this module only decides *which* id a rail cell should hold: straight north-south /
 * east-west, the four corners, and the four ascending slopes (a neighbour rail one block up). It is
 * DOM-free: callers pass a block getter and a setter (ChunkManager.setBlock hooks `settleRails` in).
 *
 * Shape numbers: 0 NS, 1 EW, 2..5 ascending east / west / north / south, 6..9 corners SE / SW / NW / NE.
 * Powered, detector and activator rails (kinds 1..6, see blocks.ts RAIL_FAMILIES) are straight or sloped only;
 * world/redstone switches a powered rail between its off (kind 1) and on (kind 2) ids.
 */

import { RAIL_FAMILIES, RAIL_KIND, RAIL_SHAPE } from '../blocks';

export type BlockGet = (x: number, y: number, z: number) => number;
export type BlockSet = (x: number, y: number, z: number, id: number) => void;

export const SHAPE_NS = 0, SHAPE_EW = 1, SHAPE_ASC_E = 2, SHAPE_ASC_W = 3, SHAPE_ASC_N = 4, SHAPE_ASC_S = 5;
export const SHAPE_SE = 6, SHAPE_SW = 7, SHAPE_NW = 8, SHAPE_NE = 9;

type V3 = readonly [number, number, number];
/** The two exits of each shape, vanilla's RailState EXITS: (dx, dy, dz) from the cell, dy 1 = the high end of a slope. */
export const EXITS: ReadonlyArray<readonly [V3, V3]> = [
  [[0, 0, -1], [0, 0, 1]], [[-1, 0, 0], [1, 0, 0]],
  [[-1, 0, 0], [1, 1, 0]], [[-1, 1, 0], [1, 0, 0]], [[0, 1, -1], [0, 0, 1]], [[0, 0, -1], [0, 1, 1]],
  [[0, 0, 1], [1, 0, 0]], [[0, 0, 1], [-1, 0, 0]], [[0, 0, -1], [-1, 0, 0]], [[0, 0, -1], [1, 0, 0]],
];

export const isRailId = (id: number): boolean => RAIL_SHAPE[id] >= 0;
export const isAscending = (shape: number): boolean => shape >= SHAPE_ASC_E && shape <= SHAPE_ASC_S;
/** Powered rails exist as straight and sloped shapes only. */
export const railIdOf = (kind: number, shape: number): number => RAIL_FAMILIES[kind][shape];

/** The four horizontal directions, N S W E, as [dx, dz]. */
const DIRS: ReadonlyArray<readonly [number, number]> = [[0, -1], [0, 1], [-1, 0], [1, 0]];

/** Port of an exit: the direction it leaves the cell and whether it leaves at the raised (+1) end. */
function hasPort(shape: number, dx: number, dz: number, up: number): boolean {
  const [a, b] = EXITS[shape];
  return (a[0] === dx && a[2] === dz && a[1] === up) || (b[0] === dx && b[2] === dz && b[1] === up);
}

/** The rail cell joined to `(x, y, z)` through exit `e`, or null: the neighbour must have its own exit facing back at the same height. */
function linked(get: BlockGet, x: number, y: number, z: number, e: V3): [number, number, number] | null {
  const h = y + e[1];
  const qx = x + e[0], qz = z + e[2];
  for (const qy of [h, h - 1]) {
    const id = get(qx, qy, qz);
    const s = RAIL_SHAPE[id];
    if (s >= 0 && hasPort(s, -e[0], -e[2], h - qy)) return [qx, qy, qz];
  }
  return null;
}

/** How many of a rail's two exits lead to a rail that leads back. */
export function realConnections(get: BlockGet, x: number, y: number, z: number): number {
  const s = RAIL_SHAPE[get(x, y, z)];
  if (s < 0) return 0;
  let n = 0;
  for (const e of EXITS[s]) if (linked(get, x, y, z, e)) n++;
  return n;
}

/** Axis of a straight / sloped shape: 0 = along z (north-south), 1 = along x (east-west), -1 for corners. */
const axisOf = (shape: number): number => shape === SHAPE_NS || shape === SHAPE_ASC_N || shape === SHAPE_ASC_S ? 0
  : shape === SHAPE_EW || shape === SHAPE_ASC_E || shape === SHAPE_ASC_W ? 1 : -1;

interface Cand { d: number; dy: number; connected: boolean }

/** Corner shape joining two perpendicular directions (indices into DIRS). */
function cornerOf(d1: number, d2: number): number {
  const has = (i: number): boolean => d1 === i || d2 === i;
  const [n, s, w, e] = [has(0), has(1), has(2), has(3)];
  return s && e ? SHAPE_SE : s && w ? SHAPE_SW : n && w ? SHAPE_NW : SHAPE_NE;
}

/** Which of the four sides could join a rail at (x, y, z) (kind 0 plain / 1 powered), best first. */
function candidates(get: BlockGet, x: number, y: number, z: number): Cand[] {
  const out: Cand[] = [];
  DIRS.forEach(([dx, dz], d) => {
    for (const dy of [0, 1, -1]) {
      const qx = x + dx, qy = y + dy, qz = z + dz;
      const qid = get(qx, qy, qz);
      const qs = RAIL_SHAPE[qid];
      if (qs < 0) continue;
      // already joined: Q has an exit facing us at the height we meet at
      const meet = (dy === 1 ? y + 1 : y); // the shared edge sits at our high end when Q is above, else at our floor
      // joined = each points at the other; a rail merely pointing at us is not (a powered rail keeps its axis)
      const selfShape = RAIL_SHAPE[get(x, y, z)];
      const connected = hasPort(qs, -dx, -dz, meet - qy) && selfShape >= 0 && hasPort(selfShape, dx, dz, dy === 1 ? 1 : 0);
      let ok = connected;
      if (!ok) {
        const free = realConnections(get, qx, qy, qz) < 2;
        // a powered neighbour keeps its axis once it has a connection
        const qPowered = RAIL_KIND[qid] === 1;
        const qAxisOk = !qPowered || axisOf(qs) === (dx !== 0 ? 1 : 0) || realConnections(get, qx, qy, qz) === 0;
        ok = free && qAxisOk;
      }
      if (ok) { out.push({ d, dy, connected }); break; }
    }
  });
  return out;
}

/** The shape a rail of `kind` should take given its neighbours; `current` is kept as the axis when nothing is near. */
export function resolveShape(get: BlockGet, x: number, y: number, z: number, kind: number, current: number): number {
  let cands = candidates(get, x, y, z);
  if (kind >= 1) {
    // powered / detector / activator: one axis only (the axis of an existing connection wins, then of the first candidate)
    const ax = (c: Cand): number => (DIRS[c.d][0] !== 0 ? 1 : 0);
    const first = cands.find((c) => c.connected) ?? cands[0];
    if (first) cands = cands.filter((c) => ax(c) === ax(first));
  }
  const dirOpp = (a: number, b: number): boolean => (a ^ 1) === b;
  const by = (d: number): Cand | undefined => cands.find((c) => c.d === d);
  const prefs: Array<[number, number]> = [[0, 1], [2, 3]];
  if (kind === 0) prefs.push([1, 3], [1, 2], [0, 2], [0, 3]);
  const valid = ([a, b]: [number, number]): boolean => {
    const ca = by(a), cb = by(b);
    if (!ca || !cb) return false;
    if (dirOpp(a, b)) return !(ca.dy === 1 && cb.dy === 1);
    return ca.dy <= 0 && cb.dy <= 0;
  };
  const connectedDirs = cands.filter((c) => c.connected).map((c) => c.d);
  let pick: [number, number] | null = null;
  // keep what is already joined, then take the best pair
  for (const need of connectedDirs.length >= 2 ? [2, 1, 0] : connectedDirs.length === 1 ? [1, 0] : [0]) {
    for (const p of prefs) {
      if (!valid(p)) continue;
      const hits = (connectedDirs.includes(p[0]) ? 1 : 0) + (connectedDirs.includes(p[1]) ? 1 : 0);
      if (hits >= need) { pick = p; break; }
    }
    if (pick) break;
  }
  let dirs: number[];
  if (pick) dirs = pick;
  else {
    // one side (or none): run straight towards it
    const c = cands.find((k) => k.connected) ?? cands[0];
    dirs = c ? [c.d] : [];
  }
  if (dirs.length === 0) {
    const ax = current >= 0 ? axisOf(current) : 0;
    return ax === 1 ? SHAPE_EW : SHAPE_NS;
  }
  if (dirs.length === 2 && !dirOpp(dirs[0], dirs[1])) return cornerOf(dirs[0], dirs[1]);
  // straight along the axis of the first direction; a neighbour one block up makes it a slope
  const axis = DIRS[dirs[0]][0] !== 0 ? 1 : 0;
  for (const d of dirs) {
    const c = by(d);
    if (c && c.dy === 1) return d === 0 ? SHAPE_ASC_N : d === 1 ? SHAPE_ASC_S : d === 2 ? SHAPE_ASC_W : SHAPE_ASC_E;
  }
  return axis === 1 ? SHAPE_EW : SHAPE_NS;
}

/**
 * Re-shape the rails around a changed cell: the cell itself (if it is a rail now) and every rail beside it,
 * cascading while shapes keep changing. `set` writes the new id (it may be ChunkManager.setBlock).
 */
export function settleRails(get: BlockGet, set: BlockSet, x: number, y: number, z: number): void {
  const queue: Array<[number, number, number]> = [];
  const seen = new Set<string>();
  const push = (qx: number, qy: number, qz: number): void => {
    const k = `${qx},${qy},${qz}`;
    if (seen.has(k) || !isRailId(get(qx, qy, qz))) return;
    seen.add(k);
    queue.push([qx, qy, qz]);
  };
  const pushAround = (cx: number, cy: number, cz: number): void => {
    for (const [dx, dz] of DIRS) for (const dy of [0, 1, -1]) push(cx + dx, cy + dy, cz + dz);
  };
  push(x, y, z);
  pushAround(x, y, z);
  for (let steps = 0; queue.length > 0 && steps < 256; steps++) {
    const [cx, cy, cz] = queue.shift()!;
    seen.delete(`${cx},${cy},${cz}`);
    const id = get(cx, cy, cz);
    const cur = RAIL_SHAPE[id];
    if (cur < 0) continue;
    const kind = RAIL_KIND[id];
    const next = resolveShape(get, cx, cy, cz, kind, cur);
    const nid = railIdOf(kind, next);
    if (nid === undefined || nid === id) continue;
    set(cx, cy, cz, nid);
    pushAround(cx, cy, cz);
  }
}

/** The rail under or at a position (vanilla getRail: the cell itself, else the one below): [x, y, z, id] or null. */
export function railAt(get: BlockGet, x: number, y: number, z: number): [number, number, number, number] | null {
  let id = get(x, y, z);
  if (RAIL_SHAPE[id] >= 0) return [x, y, z, id];
  id = get(x, y - 1, z);
  if (RAIL_SHAPE[id] >= 0) return [x, y - 1, z, id];
  return null;
}
